import React, { useCallback, useContext, useEffect, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromJid } from "@/jidUtils"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import CountryCodeSelector, {
  countries,
} from "@/components/driverManagement/CountryCodeSelector"
import { Profile } from "@/reducers/profilesSlice"
import { Copy, TriangleAlert } from "lucide-react"
import { languages } from "@/lib/languages"
import { Trans, useTranslation } from "react-i18next"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  /** When provided the modal operates in edit mode */
  user?: Profile
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}) => {
  const { t } = useTranslation()
  const isEdit = Boolean(user)
  const { client } = useContext(XmppContext)

  const vhost = client?.jid?.toString()
    ? extractVhostFromJid(client.jid.toString())
    : ""

  const usernameFromJid = user ? user.jid.split("@")[0] : ""

  const resolveInitialPhone = (phone?: string) => {
    if (!phone) return { dialCode: "+48", number: "" }
    const match = countries.find((c) => phone.startsWith(c.dialCode))
    return match
      ? { dialCode: match.dialCode, number: phone.slice(match.dialCode.length) }
      : { dialCode: "+48", number: phone }
  }

  const [username, setUsername] = useState(usernameFromJid)
  const [firstName, setFirstName] = useState(user?.firstName ?? "")
  const [lastName, setLastName] = useState(user?.lastName ?? "")
  const [phoneWithoutCode, setPhoneWithoutCode] = useState(
    () => resolveInitialPhone(user?.phone).number,
  )
  const [selectedDialCode, setSelectedDialCode] = useState(
    () => resolveInitialPhone(user?.phone).dialCode,
  )
  const initialLanguage = user?.preferredLanguage || "pl"
  const [preferredLanguage, setPreferredLanguage] = useState(initialLanguage)
  const [submitting, setSubmitting] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  )
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const { dialCode, number } = resolveInitialPhone(user?.phone)
    setUsername(usernameFromJid)
    setFirstName(user?.firstName ?? "")
    setLastName(user?.lastName ?? "")
    setPhoneWithoutCode(number)
    setSelectedDialCode(dialCode)
    setPreferredLanguage(user?.preferredLanguage || "pl")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const resetForm = useCallback(() => {
    setUsername("")
    setFirstName("")
    setLastName("")
    setPhoneWithoutCode("")
    setSelectedDialCode("+48")
    setPreferredLanguage("pl")
    setGeneratedPassword(null)
    setCopied(false)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handlePasswordDismiss = useCallback(() => {
    resetForm()
    onClose()
    onSuccess()
  }, [onClose, onSuccess, resetForm])

  const handleCopy = useCallback(() => {
    if (!generatedPassword) return
    navigator.clipboard.writeText(generatedPassword).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [generatedPassword])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await AdHocCommands.upsertOfficeUser({
        client: window.XMPP!,
        username,
        firstName,
        lastName,
        phoneNumber: phoneWithoutCode
          ? `${selectedDialCode}${phoneWithoutCode}`
          : undefined,
        // A new user always gets the chosen language. An edit sends it only
        // when the admin changed it, so saving a name change never
        // overwrites a language set elsewhere (e.g. by the user themselves).
        ...(!isEdit || preferredLanguage !== initialLanguage
          ? { preferredLanguage }
          : {}),
      })
      if (!isEdit) {
        const password = await AdHocCommands.changePassword(
          window.XMPP!,
          `${username}@${vhost}`,
        )
        setGeneratedPassword(password)
      } else {
        handleClose()
        onSuccess()
      }
    } catch (e: any) {
      const errorText = e?.text ?? e?.message ?? t("fleet.form.unknownError")
      window.appToaster.show({
        message: errorText,
        intent: "danger",
        timeout: 3000,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={generatedPassword ? undefined : handleClose}
    >
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        {generatedPassword ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {t("fleet.addUser.created")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t("fleet.password.onlyOnce")}</span>
              </div>

              <div className="space-y-2">
                <Label>{t("fleet.addUser.password")}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={generatedPassword}
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                    title={t("fleet.password.copyToClipboard")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {copied && (
                  <p className="text-xs text-muted-foreground">
                    {t("fleet.password.copied")}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={handlePasswordDismiss}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t("common.done")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {isEdit
                  ? t("fleet.addUser.titleEdit")
                  : t("fleet.addUser.titleAdd")}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">{t("fleet.addUser.login")}</Label>
                  {isEdit ? (
                    <p className="text-sm text-muted-foreground">
                      {username}@{vhost}
                    </p>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Input
                        id="username"
                        required
                        value={username}
                        onChange={(e) =>
                          setUsername(e.target.value.replace(/\s/g, ""))
                        }
                        placeholder="jan.kowalski"
                        className="flex-1"
                        autoFocus
                      />
                      {vhost && (
                        <span className="whitespace-nowrap text-sm text-muted-foreground">
                          @{vhost}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="first_name">
                    {t("fleet.form.firstName")}
                  </Label>
                  <Input
                    id="first_name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jan"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">{t("fleet.form.lastName")}</Label>
                  <Input
                    id="last_name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Kowalski"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferred_language">
                    {t("fleet.form.preferredLanguage")}
                  </Label>
                  <Select
                    value={preferredLanguage}
                    onValueChange={setPreferredLanguage}
                  >
                    <SelectTrigger
                      id="preferred_language"
                      aria-label={t("fleet.form.preferredLanguage")}
                      className="w-full"
                    >
                      <SelectValue
                        placeholder={t("fleet.form.selectLanguage")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                          <span className="flex items-center gap-2">
                            <span>{language.flag}</span>
                            <span>{language.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    <Trans
                      i18nKey="fleet.addUser.phoneOptional"
                      components={{
                        muted: <span className="text-muted-foreground" />,
                      }}
                    />
                  </Label>
                  <div className="flex gap-2">
                    <CountryCodeSelector
                      dialCode={selectedDialCode}
                      onChange={(country) =>
                        setSelectedDialCode(country.dialCode)
                      }
                    />
                    <Input
                      id="phone"
                      value={phoneWithoutCode}
                      onChange={(e) =>
                        setPhoneWithoutCode(
                          e.target.value.replace(/[^\d\s]/g, ""),
                        )
                      }
                      placeholder="682 871 329"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={submitting}
                >
                  {isEdit ? t("common.save") : t("common.add")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AddUserModal
